import React from "react";
import ReactDOM from "react-dom";
import * as XLSX from "xlsx-js-style";
import Card from "components/card";
import ActionButton from "components/ui/ActionButton";
import EmptyState from "components/ui/EmptyState";
import SectionHeader from "components/ui/SectionHeader";
import StatusBadge from "components/ui/StatusBadge";
import { useLanguage, type TranslateParams } from "context/LanguageContext";
import { authService } from "services/authService";
import { participantInsightsService } from "services/admin/participantInsightsService";
import { userService } from "services/admin/userService";
import type {
  ParticipantRole,
  ParticipantSessionRow,
  ParticipantStatus,
} from "services/admin/participantInsightsService";
import type { SessionStatus, UserListItem } from "types/domain";

type ClassOption = { id: string; name: string };

type StaffRole = Extract<ParticipantRole, "teacher" | "trainer">;
type StaffFilterRole = "all" | StaffRole;

type ShiftOption = {
  code: string;
  nameKey: string;
};

type Translate = (key: string, params?: TranslateParams) => string;

type AttendanceMetaKind =
  | "upcoming"
  | "excused"
  | "absent"
  | "late"
  | "forgotCheckout"
  | "early"
  | "onTime"
  | "unmarked"
  | "pending";

type CellValue = {
  status: ParticipantStatus;
  shiftCode: string;
  sessionStatus: SessionStatus;
};

type StaffMatrixRow = {
  key: string;
  userId: string;
  userCode: string;
  userName: string;
  userEmail: string;
  role: StaffRole;
  department: string;
  className: string;
  defaultShiftCode: string;
  cellsByDay: Record<number, CellValue>;
  sessionsByDay: Record<number, DaySession[]>;
};

type DaySession = {
  sessionId: string;
  classId: string;
  className: string;
  shiftCode: string;
  startTime: string;
  endTime: string;
  hours: number;
  status: ParticipantStatus;
  sessionStatus: SessionStatus;
  checkedInAt: string | null;
  checkedOutAt: string | null;
};

type StaffIdentity = {
  userId: string;
  userCode: string;
  userName: string;
  userEmail: string;
  role: StaffRole;
};

const SHIFT_OPTIONS: ShiftOption[] = [
  { code: "HC", nameKey: "staffAttendance.shift.HC" },
  { code: "HC7", nameKey: "staffAttendance.shift.HC7" },
  { code: "CC1330", nameKey: "staffAttendance.shift.CC1330" },
  { code: "CC1400", nameKey: "staffAttendance.shift.CC1400" },
  { code: "CC1415", nameKey: "staffAttendance.shift.CC1415" },
  { code: "CC1430", nameKey: "staffAttendance.shift.CC1430" },
  { code: "CC1500", nameKey: "staffAttendance.shift.CC1500" },
  { code: "CD", nameKey: "staffAttendance.shift.CD" },
];

const ROLE_LABEL: Record<StaffRole, string> = {
  teacher: "enum.role.TEACHER",
  trainer: "enum.role.TRAINER",
};

// Holds an i18n key; rows compare/filter on it and translate at render.
const DEPARTMENT_BY_ROLE: Record<StaffRole, string> = {
  teacher: "enum.role.TEACHER",
  trainer: "enum.role.TRAINER",
};

const STATUS_ORDER: ParticipantStatus[] = [
  "unmarked",
  "checked_in",
  "checked_out",
  "absent",
  "excused_absence",
];

const STATUS_LABEL: Record<ParticipantStatus, string> = {
  unmarked: "staffAttendance.status.unmarked",
  checked_in: "staffAttendance.status.checkedIn",
  checked_out: "staffAttendance.status.checkedOut",
  absent: "staffAttendance.status.absent",
  excused_absence: "staffAttendance.status.excused",
  other: "staffAttendance.status.other",
};

const formatAttendanceTime = (
  iso: string | null | undefined,
  locale: string
): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const sessionStatusLabelWithTime = (
  session: DaySession,
  t: Translate,
  locale: string
): string => {
  if (session.status === "checked_in") {
    const time = formatAttendanceTime(session.checkedInAt, locale);
    return time
      ? t("staffAttendance.status.checkedInAt", { time })
      : t(STATUS_LABEL.checked_in);
  }
  if (session.status === "checked_out") {
    const time = formatAttendanceTime(
      session.checkedOutAt ?? session.checkedInAt,
      locale
    );
    return time
      ? t("staffAttendance.status.checkedOutAt", { time })
      : t(STATUS_LABEL.checked_out);
  }
  return t(STATUS_LABEL[session.status] ?? STATUS_LABEL.other);
};

const STATUS_SHORT_LABEL: Record<ParticipantStatus, string> = {
  unmarked: "staffAttendance.short.unmarked",
  checked_in: "staffAttendance.short.checkedIn",
  checked_out: "staffAttendance.short.checkedOut",
  absent: "staffAttendance.short.absent",
  excused_absence: "staffAttendance.short.excused",
  other: "staffAttendance.short.other",
};

const STATUS_CELL_CLASS: Record<ParticipantStatus, string> = {
  unmarked: "bg-surface text-ink-muted border-line",
  checked_in: "bg-green-100 text-green-700 border-green-200 dark:bg-green-500/15 dark:text-green-300 dark:border-green-500/30",
  checked_out: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30",
  absent: "bg-red-100 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30",
  excused_absence: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30",
  other: "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-500/15 dark:text-purple-300 dark:border-purple-500/30",
};

const STATUS_TEXT_CLASS: Record<ParticipantStatus, string> = {
  unmarked: "text-ink-muted",
  checked_in: "text-green-700 dark:text-green-300",
  checked_out: "text-blue-700 dark:text-blue-300",
  absent: "text-red-700 dark:text-red-300",
  excused_absence: "text-amber-700 dark:text-amber-300",
  other: "text-purple-700 dark:text-purple-300",
};

const STATUS_BADGE_TONE: Record<
  ParticipantStatus,
  "info" | "success" | "warning" | "scheduled" | "danger" | "neutral"
> = {
  unmarked: "neutral",
  checked_in: "success",
  checked_out: "info",
  absent: "danger",
  excused_absence: "warning",
  other: "neutral",
};

const toInputMonth = (date: Date) => {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 7);
};

const toRangeDate = (month: string, day: number) =>
  `${month}-${String(day).padStart(2, "0")}`;

const getMonthInfo = (month: string) => {
  const [yearText, monthText] = month.split("-");
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  return { year, monthIndex, daysInMonth };
};

const WEEKDAY_KEYS = [
  "staffAttendance.weekday.sun",
  "staffAttendance.weekday.mon",
  "staffAttendance.weekday.tue",
  "staffAttendance.weekday.wed",
  "staffAttendance.weekday.thu",
  "staffAttendance.weekday.fri",
  "staffAttendance.weekday.sat",
];

// 0 = Sunday … 6 = Saturday, matching Date#getDay.
const getWeekdayIndex = (month: string, day: number) => {
  const { year, monthIndex } = getMonthInfo(month);
  return new Date(year, monthIndex, day).getDay();
};

const isWeekendDay = (month: string, day: number) => {
  const weekday = getWeekdayIndex(month, day);
  return weekday === 0 || weekday === 6;
};

const getWeekdayLabel = (month: string, day: number, t: Translate) => {
  const key = WEEKDAY_KEYS[getWeekdayIndex(month, day)];
  return key ? t(key) : "";
};

const isTodayInMonth = (month: string, day: number) => {
  const { year, monthIndex } = getMonthInfo(month);
  const now = new Date();
  return (
    now.getFullYear() === year &&
    now.getMonth() === monthIndex &&
    now.getDate() === day
  );
};

const normalizeRole = (role: ParticipantRole): StaffRole | null => {
  if (role === "teacher" || role === "trainer") return role;
  return null;
};

const statusPriority = (status: ParticipantStatus) => {
  if (status === "checked_out") return 5;
  if (status === "checked_in") return 4;
  if (status === "excused_absence") return 3;
  if (status === "absent") return 2;
  if (status === "unmarked") return 1;
  return 0;
};

const inferShiftCode = (row: ParticipantSessionRow) => {
  const titlePrefix = row.sessionTitle.split("-")[0];
  if (SHIFT_OPTIONS.some((item) => item.code === titlePrefix)) {
    return titlePrefix;
  }

  if (!row.startTime) return "HC";
  const hour = new Date(row.startTime).getHours();
  if (hour >= 22 || hour < 6) return "CD";
  if (hour >= 15) return "CC1500";
  if (hour >= 14) return "CC1400";
  if (hour >= 13) return "CC1330";
  return "HC";
};

const mergeCellValue = (
  current: CellValue | undefined,
  incoming: CellValue
): CellValue => {
  if (!current) return incoming;
  if (statusPriority(incoming.status) >= statusPriority(current.status)) {
    return incoming;
  }
  return current;
};

const compareSessionsByStartTime = (left: DaySession, right: DaySession) => {
  const leftMs = new Date(left.startTime).getTime();
  const rightMs = new Date(right.startTime).getTime();
  if (Number.isNaN(leftMs) && Number.isNaN(rightMs)) return 0;
  if (Number.isNaN(leftMs)) return 1;
  if (Number.isNaN(rightMs)) return -1;
  return leftMs - rightMs;
};

const computeHours = (startIso: string, endIso: string): number => {
  if (!startIso || !endIso) return 0;
  const startMs = new Date(startIso).getTime();
  let endMs = new Date(endIso).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return 0;
  if (endMs <= startMs) endMs += 24 * 3600 * 1000;
  const hours = (endMs - startMs) / 3_600_000;
  return Math.round(hours * 4) / 4;
};

const formatHours = (hours: number): string => {
  if (hours <= 0) return "0";
  if (Number.isInteger(hours)) return `${hours}`;
  return hours.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
};

const formatTimeRange = (startIso: string, endIso: string): string => {
  const fmt = (iso: string) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "--:--";
    return `${String(date.getHours()).padStart(2, "0")}:${String(
      date.getMinutes()
    ).padStart(2, "0")}`;
  };
  return `${fmt(startIso)} - ${fmt(endIso)}`;
};

const getLateMinutes = (session: DaySession): number => {
  if (!session.checkedInAt) return 0;
  const startMs = new Date(session.startTime).getTime();
  const checkInMs = new Date(session.checkedInAt).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(checkInMs)) return 0;
  return Math.max(0, Math.round((checkInMs - startMs) / 60000));
};

const getEarlyMinutes = (session: DaySession): number => {
  if (!session.checkedInAt) return 0;
  const startMs = new Date(session.startTime).getTime();
  const checkInMs = new Date(session.checkedInAt).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(checkInMs)) return 0;
  return Math.max(0, Math.round((startMs - checkInMs) / 60000));
};

const getDayLateStats = (sessions: DaySession[]) =>
  sessions.reduce(
    (acc, session) => {
      const lateMinutes = getLateMinutes(session);
      if (lateMinutes > 0) {
        acc.lateSessions += 1;
        acc.lateMinutes += lateMinutes;
      }
      return acc;
    },
    { lateSessions: 0, lateMinutes: 0 }
  );

const formatMinutes = (minutes: number, t: Translate): string => {
  if (minutes <= 0)
    return t("staffAttendance.duration.minutes", { minutes: 0 });
  if (minutes < 60) return t("staffAttendance.duration.minutes", { minutes });
  const hours = Math.floor(minutes / 60);
  const remain = minutes % 60;
  return remain > 0
    ? t("staffAttendance.duration.hoursMinutes", { hours, minutes: remain })
    : t("staffAttendance.duration.hours", { hours });
};

const getDayToneClass = (
  status: ParticipantStatus,
  sessions: DaySession[]
): string => {
  if (sessions.length === 0) return "";
  const now = Date.now();
  const hasForgotCheckout = sessions.some((session) => {
    const endMs = new Date(session.endTime).getTime();
    return (
      session.status === "checked_in" &&
      !session.checkedOutAt &&
      !Number.isNaN(endMs) &&
      endMs < now
    );
  });
  if (hasForgotCheckout) return "bg-rose-50/60";
  const hasAbsent = sessions.some((session) => session.status === "absent");
  if (hasAbsent) return "bg-red-50/50";
  const { lateSessions } = getDayLateStats(sessions);
  if (lateSessions > 0) return "bg-amber-50/60";
  const hasExcused = sessions.some(
    (session) => session.status === "excused_absence"
  );
  if (hasExcused) return "bg-sky-50/50";
  if (status === "checked_in" || status === "checked_out") {
    return "bg-emerald-50/60";
  }
  return "";
};

const getSessionAttendanceMeta = (
  session: DaySession,
  t: Translate
): { kind: AttendanceMetaKind; label: string; className: string } => {
  const now = Date.now();
  const startMs = new Date(session.startTime).getTime();
  const endMs = new Date(session.endTime).getTime();
  const hasStarted = !Number.isNaN(startMs) && startMs <= now;
  const hasEnded = !Number.isNaN(endMs) && endMs < now;
  const lateMinutes = getLateMinutes(session);
  const earlyMinutes = getEarlyMinutes(session);

  if (!hasStarted) {
    return {
      kind: "upcoming",
      label: t("staffAttendance.meta.upcoming"),
      className: "border-line bg-surface-muted text-ink-muted",
    };
  }

  if (session.status === "excused_absence") {
    return {
      kind: "excused",
      label: t("staffAttendance.meta.excused"),
      className: "border-sky-200 bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300 dark:border-sky-500/30",
    };
  }

  if (
    session.status === "absent" ||
    (session.status === "unmarked" && hasEnded)
  ) {
    return {
      kind: "absent",
      label: t("staffAttendance.meta.absent"),
      className: "border-red-200 bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30",
    };
  }

  if (
    (session.status === "checked_in" || session.status === "checked_out") &&
    lateMinutes > 0
  ) {
    return {
      kind: "late",
      label: t("staffAttendance.meta.late", {
        duration: formatMinutes(lateMinutes, t),
      }),
      className: "border-amber-200 bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30",
    };
  }

  if (session.status === "checked_in" && hasEnded && !session.checkedOutAt) {
    return {
      kind: "forgotCheckout",
      label: t("staffAttendance.meta.forgotCheckout"),
      className: "border-rose-200 bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/30",
    };
  }

  if (session.status === "checked_in" || session.status === "checked_out") {
    if (earlyMinutes > 0) {
      return {
        kind: "early",
        label: t("staffAttendance.meta.early", {
          duration: formatMinutes(earlyMinutes, t),
        }),
        className: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30",
      };
    }
    return {
      kind: "onTime",
      label: t("staffAttendance.meta.onTime"),
      className: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30",
    };
  }

  if (hasEnded) {
    return {
      kind: "unmarked",
      label: t("staffAttendance.meta.unmarked"),
      className: "border-line bg-surface-muted text-ink-muted",
    };
  }

  return {
    kind: "pending",
    label: t("staffAttendance.meta.pending"),
    className: "border-line bg-surface-muted text-ink-muted",
  };
};

const getSessionLateText = (
  session: DaySession,
  t: Translate
): string | null => {
  const lateMinutes = getLateMinutes(session);
  if (lateMinutes <= 0) return null;
  return t("staffAttendance.meta.late", {
    duration: formatMinutes(lateMinutes, t),
  });
};

const hasSessionStarted = (session: DaySession): boolean => {
  const startMs = new Date(session.startTime).getTime();
  return !Number.isNaN(startMs) && startMs <= Date.now();
};

const hasSessionEnded = (session: DaySession): boolean => {
  const endMs = new Date(session.endTime).getTime();
  return !Number.isNaN(endMs) && endMs < Date.now();
};

const toExcelSafeSheetName = (name: string, fallback: string) => {
  const cleaned = name.replace(/[:\\/?*\[\]]/g, " ").trim();
  return (cleaned || fallback).slice(0, 31);
};

const appendPersonalAttendanceSheet = (
  workbook: XLSX.WorkBook,
  staffRow: StaffMatrixRow,
  year: string,
  month: string,
  selectedMonth: string,
  allDayColumns: number[],
  hideEmptyDays: boolean,
  t: Translate,
  locale: string,
  usedSheetNames?: Set<string>
) => {
  const baseName = toExcelSafeSheetName(
    staffRow.userName,
    t("staffAttendance.export.defaultSheetName")
  );
  let sheetName = baseName;
  if (usedSheetNames) {
    let counter = 2;
    while (usedSheetNames.has(sheetName)) {
      const suffix = ` (${counter})`;
      const trimmed = baseName.slice(0, 31 - suffix.length);
      sheetName = `${trimmed}${suffix}`;
      counter += 1;
    }
    usedSheetNames.add(sheetName);
  }

  const getDaySessions = (day: number) => {
    const sessions = staffRow.sessionsByDay[day] ?? [];
    return hideEmptyDays ? sessions.filter((s) => s.checkedInAt) : sessions;
  };
  const dayColumns = hideEmptyDays
    ? allDayColumns.filter((day) => getDaySessions(day).length > 0)
    : allDayColumns;

  const maxSessionsInDay = Math.max(
    1,
    ...dayColumns.map((day) => getDaySessions(day).length)
  );
  const totalCols = Math.max(6, 1 + maxSessionsInDay);

  // Summary counts always reflect the full month, regardless of the
  // display-only day filter above.
  const lateSessionsCount = allDayColumns.reduce((acc, day) => {
    const sessions = staffRow.sessionsByDay[day] ?? [];
    return acc + sessions.filter((s) => getLateMinutes(s) > 0).length;
  }, 0);
  const lateMinutesTotal = allDayColumns.reduce((acc, day) => {
    const sessions = staffRow.sessionsByDay[day] ?? [];
    return acc + sessions.reduce((sum, s) => sum + getLateMinutes(s), 0);
  }, 0);
  const presentCount = allDayColumns.reduce((acc, day) => {
    const sessions = staffRow.sessionsByDay[day] ?? [];
    return (
      acc +
      sessions.filter(
        (s) => s.status === "checked_in" || s.status === "checked_out"
      ).length
    );
  }, 0);
  const absentCount = allDayColumns.reduce((acc, day) => {
    const sessions = staffRow.sessionsByDay[day] ?? [];
    return acc + sessions.filter((s) => s.status === "absent").length;
  }, 0);
  const leaveCount = allDayColumns.reduce((acc, day) => {
    const sessions = staffRow.sessionsByDay[day] ?? [];
    return acc + sessions.filter((s) => s.status === "excused_absence").length;
  }, 0);

  const worksheetData: string[][] = [
    [t("staffAttendance.export.personalTitle", { month, year })],
    [staffRow.userName],
    [t(staffRow.department)],
    [
      t("staffAttendance.export.codeEmail", {
        code: staffRow.userCode,
        email: staffRow.userEmail,
      }),
    ],
    [],
    [
      t("staffAttendance.export.presentCount", { count: presentCount }),
      t("staffAttendance.export.lateSessionsCount", {
        count: lateSessionsCount,
      }),
      t("staffAttendance.export.lateMinutesTotal", {
        duration: formatMinutes(lateMinutesTotal, t),
      }),
      t("staffAttendance.export.absentCount", { count: absentCount }),
      t("staffAttendance.export.leaveCount", { count: leaveCount }),
      ...Array.from({ length: totalCols - 5 }, () => ""),
    ],
    [],
    [
      t("staffAttendance.col.date"),
      ...Array.from({ length: totalCols - 1 }, (_, index) =>
        t("staffAttendance.export.shiftN", { index: index + 1 })
      ),
    ],
  ];

  const tableBorder = {
    top: { style: "thin", color: { rgb: "C7D2E3" } },
    bottom: { style: "thin", color: { rgb: "C7D2E3" } },
    left: { style: "thin", color: { rgb: "C7D2E3" } },
    right: { style: "thin", color: { rgb: "C7D2E3" } },
  };

  const outerBorder = {
    top: { style: "medium", color: { rgb: "B8C6DB" } },
    bottom: { style: "medium", color: { rgb: "B8C6DB" } },
    left: { style: "medium", color: { rgb: "B8C6DB" } },
    right: { style: "medium", color: { rgb: "B8C6DB" } },
  };

  const summaryFill = { fgColor: { rgb: "EFF6FF" } };
  const titleFill = { fgColor: { rgb: "F8FBFF" } };
  const weekendFill = { fgColor: { rgb: "FFF9E6" } };
  const defaultFill = { fgColor: { rgb: "FFFFFF" } };

  const getSessionExcelColors = (session: DaySession) => {
    const meta = getSessionAttendanceMeta(session, t);
    if (meta.kind === "absent") {
      return {
        fill: { fgColor: { rgb: "FEF2F2" } },
        font: { color: { rgb: "DC2626" } },
      };
    }
    if (meta.kind === "excused") {
      return {
        fill: { fgColor: { rgb: "FEF3C7" } },
        font: { color: { rgb: "B45309" } },
      };
    }
    if (meta.kind === "forgotCheckout") {
      return {
        fill: { fgColor: { rgb: "F8FAFC" } },
        font: { color: { rgb: "374151" } },
      };
    }
    if (meta.kind === "upcoming" || meta.kind === "pending") {
      return {
        fill: { fgColor: { rgb: "F8FAFC" } },
        font: { color: { rgb: "6B7280" } },
      };
    }
    if (session.status === "checked_out") {
      return {
        fill: { fgColor: { rgb: "EFF6FF" } },
        font: { color: { rgb: "2563EB" } },
      };
    }
    if (session.status === "checked_in") {
      return {
        fill: { fgColor: { rgb: "ECFDF5" } },
        font: { color: { rgb: "059669" } },
      };
    }
    return { fill: defaultFill, font: { color: { rgb: "374151" } } };
  };

  const rowMeta = dayColumns.map((day) => {
    const sessions = getDaySessions(day);
    const weekday = getWeekdayLabel(selectedMonth, day, t);
    const isWeekend = isWeekendDay(selectedMonth, day);
    const rowFill = isWeekend ? weekendFill : defaultFill;

    const dayCell =
      sessions.length > 0
        ? `${String(day).padStart(2, "0")}/${month} (${weekday})\n${t(
            "staffAttendance.sessionCount",
            { count: sessions.length }
          )}`
        : `${String(day).padStart(2, "0")}/${month} (${weekday})`;

    const rowValues = [dayCell];
    for (let index = 0; index < totalCols - 1; index += 1) {
      const session = sessions[index];
      if (!session) {
        rowValues.push("");
        continue;
      }
      const statusLabel = getSessionAttendanceMeta(session, t).label;
      const lateText = getSessionLateText(session, t);
      rowValues.push(
        [
          session.className,
          formatTimeRange(session.startTime, session.endTime),
          t("staffAttendance.export.inOut", {
            checkIn:
              formatAttendanceTime(session.checkedInAt, locale) ?? "--:--",
            checkOut:
              formatAttendanceTime(session.checkedOutAt, locale) ?? "--:--",
          }),
          statusLabel,
          lateText ?? "",
        ]
          .filter(Boolean)
          .join("\n")
      );
    }
    const rowIndex = worksheetData.length;
    worksheetData.push(rowValues);
    return { row: rowIndex, fill: rowFill, sessions, isWeekend };
  });

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  worksheet["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: totalCols - 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: totalCols - 1 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: totalCols - 1 } },
  ];
  worksheet["!cols"] = [
    { wch: 20 },
    ...Array.from({ length: totalCols - 1 }, () => ({ wch: 30 })),
  ];
  worksheet["!rows"] = [
    { hpt: 26 },
    { hpt: 22 },
    { hpt: 20 },
    { hpt: 20 },
    { hpt: 8 },
    { hpt: 24 },
    { hpt: 10 },
    { hpt: 24 },
    ...rowMeta.map((meta) => ({ hpt: meta.sessions.length > 0 ? 74 : 28 })),
  ];

  const setCellStyle = (address: string, style: Record<string, unknown>) => {
    const cell = worksheet[address];
    if (!cell) return;
    cell.s = style;
  };

  setCellStyle("A1", {
    font: { bold: true, sz: 14, color: { rgb: "2F5597" } },
    alignment: { horizontal: "left", vertical: "center" },
    fill: titleFill,
  });
  setCellStyle("A2", {
    font: { bold: true, sz: 13, color: { rgb: "111827" } },
    alignment: { horizontal: "left", vertical: "center" },
    fill: titleFill,
  });
  setCellStyle("A3", {
    font: { sz: 12, color: { rgb: "4B5563" }, bold: true },
    alignment: { horizontal: "left", vertical: "center" },
    fill: titleFill,
  });
  setCellStyle("A4", {
    font: { sz: 10, color: { rgb: "6B7280" } },
    alignment: { horizontal: "left", vertical: "center" },
    fill: titleFill,
  });

  for (let col = 0; col < totalCols; col += 1) {
    const summaryAddress = XLSX.utils.encode_cell({ r: 5, c: col });
    setCellStyle(summaryAddress, {
      font: { bold: col < 5, sz: 11 },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: col < 5 ? outerBorder : tableBorder,
      fill: col < 5 ? summaryFill : defaultFill,
    });
    const headerAddress = XLSX.utils.encode_cell({ r: 7, c: col });
    setCellStyle(headerAddress, {
      font: { bold: true, sz: 11 },
      alignment: { horizontal: "center", vertical: "center" },
      border: outerBorder,
      fill: { fgColor: { rgb: "F8FAFC" } },
    });
  }

  rowMeta.forEach((meta) => {
    for (let col = 0; col < totalCols; col += 1) {
      const address = XLSX.utils.encode_cell({ r: meta.row, c: col });
      const isOuterCol = col === 0 || col === totalCols - 1;
      setCellStyle(address, {
        alignment: {
          horizontal: col === 0 ? "left" : "center",
          vertical: "top",
          wrapText: true,
        },
        border: isOuterCol ? outerBorder : tableBorder,
        fill: meta.fill,
        font: {
          sz: col === 0 ? 11 : 10,
          color: { rgb: col === 0 ? "1F2937" : "374151" },
          bold: col === 0,
        },
      });
    }

    meta.sessions.forEach((session, index) => {
      const address = XLSX.utils.encode_cell({ r: meta.row, c: 1 + index });
      const sessionColors = getSessionExcelColors(session);
      setCellStyle(address, {
        alignment: { horizontal: "left", vertical: "top", wrapText: true },
        border:
          index === meta.sessions.length - 1 && 1 + index === totalCols - 1
            ? outerBorder
            : tableBorder,
        fill: sessionColors.fill ?? meta.fill,
        font: {
          sz: 10,
          color: sessionColors.font?.color ?? { rgb: "1F2937" },
        },
      });

      const cell = worksheet[address];
      if (cell) {
        cell.v = [
          session.className,
          formatTimeRange(session.startTime, session.endTime),
          t("staffAttendance.export.inOut", {
            checkIn:
              formatAttendanceTime(session.checkedInAt, locale) ?? "--:--",
            checkOut:
              formatAttendanceTime(session.checkedOutAt, locale) ?? "--:--",
          }),
          getSessionAttendanceMeta(session, t).label,
          getSessionLateText(session, t) ?? "",
        ]
          .filter(Boolean)
          .join("\n");
        cell.t = "s";
      }
    });

    for (let col = 0; col < totalCols; col += 1) {
      const address = XLSX.utils.encode_cell({ r: meta.row, c: col });
      const cell = worksheet[address];
      if (!cell) continue;
      const border = { ...(cell.s?.border as object) } as Record<
        string,
        unknown
      >;
      if (col === 0) border.left = outerBorder.left;
      if (col === totalCols - 1) border.right = outerBorder.right;
      if (meta.row === 8) border.top = outerBorder.top;
      if (meta.row === 7 + rowMeta.length) border.bottom = outerBorder.bottom;
      cell.s = {
        ...(cell.s || {}),
        border,
      };
    }
  });

  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  return sheetName;
};

const getDayWorkedHours = (sessions: DaySession[]): number =>
  sessions
    .filter(
      (session) =>
        session.status === "checked_in" || session.status === "checked_out"
    )
    .reduce((acc, session) => acc + session.hours, 0);

type CellEditorProps = {
  sessions: DaySession[];
  staffName: string;
  day: number;
  month: string;
  onClose: () => void;
};

const CellEditor = ({
  sessions,
  staffName,
  day,
  month,
  onClose,
}: CellEditorProps): JSX.Element | null => {
  const { t, locale } = useLanguage();
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const [, monthText] = month.split("-");
  const dateLabel = `${String(day).padStart(2, "0")}/${monthText}`;

  const popup = (
    <div
      className="bg-slate-900/35 fixed inset-0 z-[1000] flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md rounded-xl border border-line bg-surface p-4 text-left shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-ink">{staffName}</p>
            <p className="text-xs text-ink-muted">
              {t("staffAttendance.dayLabel", { date: dateLabel })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-brand-50 px-2 py-1 text-[11px] font-semibold text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
              {t("staffAttendance.sessionCount", { count: sessions.length })}
            </span>
            <ActionButton
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="!rounded-md !px-2 !py-1 text-ink-subtle hover:text-gray-600"
            >
              {t("action.close")}
            </ActionButton>
          </div>
        </div>

        {sessions.length > 0 ? (
          <div className="mb-3 max-h-48 space-y-1 overflow-auto rounded-md border border-line bg-gray-50/60 p-1">
            {sessions.map((session) => (
              <div
                key={session.sessionId}
                className="rounded bg-surface p-2 shadow-sm"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold text-ink">
                    {session.className}
                  </span>
                  <StatusBadge
                    label={`${t(
                      STATUS_SHORT_LABEL[session.status]
                    )} ${sessionStatusLabelWithTime(session, t, locale)}`}
                    tone={STATUS_BADGE_TONE[session.status]}
                    className="!px-2 !py-[1px] !text-[10px]"
                  />
                </div>
                <div className="mt-1 flex items-center justify-between text-[10px] text-ink-muted">
                  <span>
                    {formatTimeRange(session.startTime, session.endTime)}
                  </span>
                  <span>
                    {t("staffAttendance.inOutInline", {
                      checkIn:
                        formatAttendanceTime(session.checkedInAt, locale) ??
                        "--:--",
                      checkOut:
                        formatAttendanceTime(session.checkedOutAt, locale) ??
                        "--:--",
                    })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="mb-3 rounded-md border border-dashed border-line px-2 py-3 text-center text-[11px] text-ink-subtle">
            {t("staffAttendance.noSessionsOnDay")}
          </p>
        )}
      </div>
    </div>
  );

  if (typeof document === "undefined") return popup;
  return ReactDOM.createPortal(popup, document.body);
};

interface StaffAttendanceProps {
  // When provided, scope class list + participant rows to the given center.
  // Used by System Admin acting-as-center-owner.
  centerId?: string;
}

const StaffAttendance = ({
  centerId,
}: StaffAttendanceProps = {}): JSX.Element => {
  const { t, locale } = useLanguage();
  const currentMonth = React.useMemo(() => toInputMonth(new Date()), []);
  const currentUser = React.useMemo(() => authService.getStoredUser(), []);
  const isSelfOnlyScope = React.useMemo(() => {
    const role = currentUser?.role?.toUpperCase();
    return role === "TEACHER" || role === "TRAINER" || role === "STUDENT";
  }, [currentUser]);

  const [selectedMonth, setSelectedMonth] = React.useState(currentMonth);
  const [classId, setClassId] = React.useState("all");
  const [roleFilter, setRoleFilter] = React.useState<StaffFilterRole>("all");
  const [search, setSearch] = React.useState("");
  // For trainers, default to their own ID; for others, default to "all"
  const [selectedStaffId, setSelectedStaffId] = React.useState<string | "all">(
    isSelfOnlyScope && currentUser?.id ? currentUser.id : "all"
  );
  const [expandedDays, setExpandedDays] = React.useState<Set<string>>(
    new Set()
  );
  const [exportModalOpen, setExportModalOpen] = React.useState(false);
  const [selectedExportIds, setSelectedExportIds] = React.useState<Set<string>>(
    new Set()
  );
  const [modalSearch, setModalSearch] = React.useState("");
  const [modalClassFilter, setModalClassFilter] = React.useState<string>("all");
  const [modalDepartmentFilter, setModalDepartmentFilter] =
    React.useState<string>("all");
  // Default on: most months have weekends/holidays with no shift for anyone,
  // and showing those empty columns just adds noise to the matrix.
  const [hideEmptyDays, setHideEmptyDays] = React.useState(true);

  const [classes, setClasses] = React.useState<ClassOption[]>([]);
  const [rows, setRows] = React.useState<ParticipantSessionRow[]>([]);
  const [staffUsers, setStaffUsers] = React.useState<StaffIdentity[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [openCellKey, setOpenCellKey] = React.useState<string | null>(null);

  const monthInfo = React.useMemo(
    () => getMonthInfo(selectedMonth),
    [selectedMonth]
  );
  const dayColumns = React.useMemo(
    () => Array.from({ length: monthInfo.daysInMonth }, (_, idx) => idx + 1),
    [monthInfo.daysInMonth]
  );

  // Class options are derived directly from the session-participant rows
  // (session.class_ is already embedded in that response), so no separate
  // /classes call is needed just to build the dropdown.
  const deriveStaffUsers = (
    participantRows: ParticipantSessionRow[]
  ): StaffIdentity[] => {
    const merged = new Map<string, StaffIdentity>();
    participantRows.forEach((row) => {
      if (row.role !== "teacher" && row.role !== "trainer") return;
      if (merged.has(row.userId)) return;
      merged.set(row.userId, {
        userId: row.userId,
        userCode: row.userCode?.trim() || row.userId.slice(0, 6).toUpperCase(),
        userName: row.userName,
        userEmail: row.userEmail,
        role: row.role,
      });
    });
    return Array.from(merged.values()).sort((a, b) =>
      a.userName.localeCompare(b.userName, "vi")
    );
  };

  const mapStaffRole = (role: UserListItem["role"]): StaffRole | null => {
    if (role === "TEACHER") return "teacher";
    if (role === "TRAINER") return "trainer";
    return null;
  };

  // Staff who have zero sessions in the selected month never show up in the
  // participant rows, but should still appear as a zero-attendance row when
  // browsing a specific center's roster — so fetch the roster and merge it
  // with whatever attendance-derived identities we already have.
  const loadStaffRoster = React.useCallback(async (): Promise<
    StaffIdentity[]
  > => {
    const limit = 200;
    const [teacherPage, trainerPage] = await Promise.all([
      userService.getAll({
        role: "TEACHER",
        skip: 0,
        limit,
        center_id: centerId,
      }),
      userService.getAll({
        role: "TRAINER",
        skip: 0,
        limit,
        center_id: centerId,
      }),
    ]);

    const merged = new Map<string, StaffIdentity>();
    [...teacherPage.items, ...trainerPage.items].forEach((user) => {
      const role = mapStaffRole(user.role);
      if (!role) return;
      merged.set(user.id, {
        userId: user.id,
        userCode: user.code?.trim() || user.id.slice(0, 6).toUpperCase(),
        userName: user.full_name,
        userEmail: user.email ?? "",
        role,
      });
    });
    return Array.from(merged.values());
  }, [centerId]);

  const deriveClasses = (
    participantRows: ParticipantSessionRow[]
  ): ClassOption[] => {
    const merged = new Map<string, ClassOption>();
    participantRows.forEach((row) => {
      if (!row.classId || merged.has(row.classId)) return;
      merged.set(row.classId, { id: row.classId, name: row.className });
    });
    return Array.from(merged.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "vi")
    );
  };

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const fromDate = toRangeDate(selectedMonth, 1);
      const toDate = toRangeDate(selectedMonth, monthInfo.daysInMonth);

      // Fetch the full month unfiltered by class — the class dropdown itself
      // is derived from this result, and matrixRows already applies the
      // classId filter client-side, so filtering here would shrink the
      // dropdown to whatever class was last selected.
      // Always fetch the full staff roster (via /users) alongside attendance —
      // a teacher/trainer with zero sessions this month has no participant
      // row at all, so without the roster they'd be invisible entirely.
      const [participantRows, roster] = await Promise.all([
        participantInsightsService.getParticipantRows({
          fromDate,
          toDate,
          roles: ["teacher", "trainer"],
          centerId,
          t,
        }),
        loadStaffRoster(),
      ]);

      // Keep all session rows in the selected month, including upcoming /
      // unmarked sessions, so the personal attendance board still shows
      // planned shifts before attendance is recorded.
      // Trainers/teachers can only see their own data.
      const scopedRows =
        isSelfOnlyScope && currentUser?.id
          ? participantRows.filter((item) => item.userId === currentUser.id)
          : participantRows;

      // The roster (from /users) reflects each user's real account role.
      // A session-participant row only reflects the role they were added as
      // on that specific session, which can drift from their account role
      // (e.g. a teacher covering a session as "trainer") — so roster wins
      // and only fills in identities for users the roster didn't already
      // have (roster is skipped when centerId isn't set, see above).
      const attendanceStaff = deriveStaffUsers(scopedRows);
      const mergedStaff = new Map<string, StaffIdentity>();
      attendanceStaff.forEach((staff) => mergedStaff.set(staff.userId, staff));
      roster.forEach((staff) => mergedStaff.set(staff.userId, staff));
      const scopedStaff = isSelfOnlyScope
        ? Array.from(mergedStaff.values()).filter(
            (staff) => staff.userId === currentUser?.id
          )
        : Array.from(mergedStaff.values()).sort((a, b) =>
            a.userName.localeCompare(b.userName, "vi")
          );

      setClasses(deriveClasses(scopedRows));
      setRows(scopedRows);
      setStaffUsers(scopedStaff);
    } catch {
      setError("staffAttendance.loadFailed");
      setClasses([]);
      setRows([]);
      setStaffUsers([]);
    } finally {
      setLoading(false);
    }
  }, [
    centerId,
    currentUser?.id,
    isSelfOnlyScope,
    loadStaffRoster,
    monthInfo.daysInMonth,
    selectedMonth,
    t,
  ]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    if (selectedStaffId === "all") {
      setExpandedDays(new Set());
    }
  }, [selectedStaffId]);

  const matrixRows = React.useMemo(() => {
    const keyword = search.trim().toLowerCase();
    const map = new Map<string, StaffMatrixRow>();

    staffUsers.forEach((staff) => {
      if (
        isSelfOnlyScope &&
        currentUser?.id &&
        staff.userId !== currentUser.id
      ) {
        return;
      }
      if (selectedStaffId !== "all" && staff.userId !== selectedStaffId) return;
      if (roleFilter !== "all" && staff.role !== roleFilter) return;
      const userBlock = [staff.userCode, staff.userName, staff.userEmail]
        .join(" ")
        .toLowerCase();
      if (keyword && !userBlock.includes(keyword)) return;

      const key = staff.userId;
      map.set(key, {
        key,
        userId: staff.userId,
        userCode: staff.userCode,
        userName: staff.userName,
        userEmail: staff.userEmail,
        role: staff.role,
        department: DEPARTMENT_BY_ROLE[staff.role],
        className: "",
        defaultShiftCode: "HC",
        cellsByDay: {},
        sessionsByDay: {},
      });
    });

    rows.forEach((row) => {
      if (isSelfOnlyScope && currentUser?.id && row.userId !== currentUser.id) {
        return;
      }
      if (selectedStaffId !== "all" && row.userId !== selectedStaffId) return;
      if (classId !== "all" && row.classId !== classId) return;
      if (!row.startTime) return;

      const key = row.userId;
      const exists = map.get(key);
      const role = exists?.role ?? normalizeRole(row.role);
      if (!role) return;
      if (roleFilter !== "all" && role !== roleFilter) return;

      const date = new Date(row.startTime);
      if (
        Number.isNaN(date.getTime()) ||
        date.getMonth() !== monthInfo.monthIndex ||
        date.getFullYear() !== monthInfo.year
      ) {
        return;
      }

      const day = date.getDate();
      const shiftCode = inferShiftCode(row);
      const cellValue: CellValue = {
        status: row.status,
        shiftCode,
        sessionStatus: row.sessionStatus,
      };
      const session: DaySession = {
        sessionId: row.sessionId,
        classId: row.classId,
        className: row.className,
        shiftCode,
        startTime: row.startTime,
        endTime: row.endTime ?? row.startTime,
        hours: computeHours(row.startTime, row.endTime ?? row.startTime),
        status: row.status,
        sessionStatus: row.sessionStatus,
        checkedInAt: row.checkedInAt ?? null,
        checkedOutAt: row.checkedOutAt ?? null,
      };

      if (!exists) {
        const userCode =
          row.userCode?.trim() || row.userId.slice(0, 6).toUpperCase();
        const userBlock = [userCode, row.userName, row.userEmail, row.className]
          .join(" ")
          .toLowerCase();
        if (keyword && !userBlock.includes(keyword)) return;

        map.set(key, {
          key,
          userId: row.userId,
          userCode,
          userName: row.userName,
          userEmail: row.userEmail,
          role,
          department: DEPARTMENT_BY_ROLE[role],
          className: row.className,
          defaultShiftCode: shiftCode,
          cellsByDay: { [day]: cellValue },
          sessionsByDay: { [day]: [session] },
        });
      } else {
        const userBlock = [
          exists.userCode,
          exists.userName,
          exists.userEmail,
          exists.className,
        ]
          .join(" ")
          .toLowerCase();
        if (keyword && !userBlock.includes(keyword)) return;
        if (!exists.className) {
          exists.className = row.className;
        }
        exists.cellsByDay[day] = mergeCellValue(
          exists.cellsByDay[day],
          cellValue
        );
        const list = exists.sessionsByDay[day] ?? [];
        list.push(session);
        exists.sessionsByDay[day] = list.sort(compareSessionsByStartTime);
      }
    });

    map.forEach((entry) => {
      Object.keys(entry.sessionsByDay).forEach((dayKey) => {
        entry.sessionsByDay[Number(dayKey)] = entry.sessionsByDay[
          Number(dayKey)
        ].sort(compareSessionsByStartTime);
      });
    });

    return Array.from(map.values()).sort((left, right) =>
      left.userName.localeCompare(right.userName, "vi")
    );
  }, [
    classId,
    currentUser?.id,
    isSelfOnlyScope,
    monthInfo.monthIndex,
    monthInfo.year,
    roleFilter,
    rows,
    search,
    staffUsers,
    selectedStaffId,
  ]);

  // Show the flat single-staff list whenever exactly one person is picked —
  // either explicitly via the "Nhân sự" dropdown, or implicitly because the
  // class/role/search filters have narrowed the result down to one person.
  const showSingleStaffView =
    selectedStaffId !== "all" || matrixRows.length === 1;

  // A day only counts as "has attendance" once someone actually checked in —
  // a scheduled-but-absent session shouldn't keep the day column/row visible.
  const rowHasAttendanceOnDay = React.useCallback(
    (row: StaffMatrixRow, day: number) =>
      (row.sessionsByDay[day] ?? []).some((session) => session.checkedInAt),
    []
  );

  const getVisibleDaysForRow = React.useCallback(
    (row: StaffMatrixRow) =>
      hideEmptyDays
        ? dayColumns.filter((day) => rowHasAttendanceOnDay(row, day))
        : dayColumns,
    [dayColumns, hideEmptyDays, rowHasAttendanceOnDay]
  );

  // Shared day-column set for the multi-staff matrix/mobile views — a day is
  // dropped only when nobody in the currently filtered staff list checked in.
  const visibleDayColumns = React.useMemo(
    () =>
      hideEmptyDays
        ? dayColumns.filter((day) =>
            matrixRows.some((row) => rowHasAttendanceOnDay(row, day))
          )
        : dayColumns,
    [dayColumns, hideEmptyDays, matrixRows, rowHasAttendanceOnDay]
  );

  const getCellValue = React.useCallback(
    (row: StaffMatrixRow, day: number): CellValue => {
      return (
        row.cellsByDay[day] ??
        ({
          status: "unmarked",
          shiftCode: row.defaultShiftCode,
          sessionStatus: "SCHEDULED",
        } as CellValue)
      );
    },
    []
  );

  const getRowStats = React.useCallback(
    (row: StaffMatrixRow) => {
      let present = 0;
      let absent = 0;
      let leave = 0;
      let totalHours = 0;
      let lateSessions = 0;
      let lateMinutes = 0;

      dayColumns.forEach((day) => {
        const sessions = row.sessionsByDay[day] ?? [];
        totalHours += getDayWorkedHours(sessions);

        sessions.forEach((session) => {
          if (
            session.status === "checked_in" ||
            session.status === "checked_out"
          ) {
            present += 1;
          } else if (session.status === "excused_absence") {
            leave += 1;
          } else if (
            session.status === "absent" ||
            (session.status === "unmarked" && hasSessionEnded(session))
          ) {
            absent += 1;
          }

          const lateMinutesForSession = getLateMinutes(session);
          if (
            lateMinutesForSession > 0 &&
            (session.status === "checked_in" ||
              session.status === "checked_out")
          ) {
            lateSessions += 1;
            lateMinutes += lateMinutesForSession;
          }
        });
      });

      return { present, absent, leave, totalHours, lateSessions, lateMinutes };
    },
    [dayColumns]
  );

  const summary = React.useMemo(() => {
    const totalStaff = matrixRows.length;
    let totalPresent = 0;
    let totalAbsent = 0;
    let totalLeave = 0;
    let totalHours = 0;
    let totalLateSessions = 0;
    let totalLateMinutes = 0;

    matrixRows.forEach((row) => {
      const stats = getRowStats(row);
      totalPresent += stats.present;
      totalAbsent += stats.absent;
      totalLeave += stats.leave;
      totalHours += stats.totalHours;
      totalLateSessions += stats.lateSessions;
      totalLateMinutes += stats.lateMinutes;
    });

    return {
      totalStaff,
      totalPresent,
      totalAbsent,
      totalLeave,
      totalHours,
      totalLateSessions,
      totalLateMinutes,
    };
  }, [getRowStats, matrixRows]);

  const summaryItems = React.useMemo(
    () => [
      {
        key: "staff",
        label: t("staffAttendance.summary.staff"),
        value: String(summary.totalStaff),
        valueClassName: "text-ink",
      },
      {
        key: "present",
        label: t("staffAttendance.summary.present"),
        value: String(summary.totalPresent),
        valueClassName: "text-green-600 dark:text-green-300",
      },
      {
        key: "lateSessions",
        label: t("staffAttendance.summary.lateSessions"),
        value: String(summary.totalLateSessions),
        valueClassName: "text-amber-600 dark:text-amber-300",
      },
      {
        key: "lateMinutes",
        label: t("staffAttendance.summary.lateMinutes"),
        value: formatMinutes(summary.totalLateMinutes, t),
        valueClassName: "text-orange-600 dark:text-orange-300",
      },
      {
        key: "absent",
        label: t("staffAttendance.summary.absent"),
        value: String(summary.totalAbsent),
        valueClassName: "text-red-600 dark:text-red-300",
      },
      {
        key: "leave",
        label: t("staffAttendance.summary.leave"),
        value: String(summary.totalLeave),
        valueClassName: "text-blue-600 dark:text-blue-300",
      },
    ],
    [summary, t]
  );

  const handleExportExcel = React.useCallback(
    (userIds?: Set<string>) => {
      const rowsForExport = userIds
        ? matrixRows.filter((row) => userIds.has(row.userId))
        : matrixRows;
      const [year, month] = selectedMonth.split("-");

      if (selectedStaffId !== "all" && rowsForExport.length === 1) {
        const staffRow = rowsForExport[0];
        const workbook = XLSX.utils.book_new();
        appendPersonalAttendanceSheet(
          workbook,
          staffRow,
          year,
          month,
          selectedMonth,
          dayColumns,
          hideEmptyDays,
          t,
          locale
        );
        XLSX.writeFile(
          workbook,
          `bang-cham-cong-${staffRow.userCode}-${month}-${year}.xlsx`
        );
        return;
      }

      const exportDayColumns = hideEmptyDays
        ? dayColumns.filter((day) =>
            rowsForExport.some((row) => rowHasAttendanceOnDay(row, day))
          )
        : dayColumns;

      const header = [
        t("staffAttendance.col.index"),
        t("staffAttendance.col.fullName"),
        ...exportDayColumns.map((day) => {
          const weekday = getWeekdayLabel(selectedMonth, day, t);
          return `${String(day).padStart(2, "0")}/${month} (${weekday})`;
        }),
        t("staffAttendance.stat.present"),
        t("staffAttendance.stat.absent"),
        t("staffAttendance.stat.leave"),
        t("staffAttendance.col.totalHours"),
      ];

      const body = rowsForExport.map((row, index) => {
        const stats = getRowStats(row);
        const dayValues = exportDayColumns.map((day) => {
          const daySessions = row.sessionsByDay[day] ?? [];
          const sessions = hideEmptyDays
            ? daySessions.filter((s) => s.checkedInAt)
            : daySessions;
          if (sessions.length === 0) return "";
          const lines: string[] = [];
          for (const session of sessions) {
            const inTime =
              formatAttendanceTime(session.checkedInAt, locale) ?? "--:--";
            const outTime =
              formatAttendanceTime(session.checkedOutAt, locale) ?? "--:--";
            lines.push(session.className);
            lines.push(`${inTime} / ${outTime}`);
            const lateText = getSessionLateText(session, t);
            if (lateText) lines.push(lateText);
          }
          return lines.join("\n");
        });

        const nameLines = [row.userName];
        if (row.department) nameLines.push(t(row.department));
        if (row.userEmail) nameLines.push(row.userEmail);
        const nameWithPosition = nameLines.join("\n");

        return [
          index + 1,
          nameWithPosition,
          ...dayValues,
          stats.present,
          stats.absent,
          stats.leave,
          formatHours(stats.totalHours),
        ];
      });

      const worksheet = XLSX.utils.aoa_to_sheet([header, ...body]);
      worksheet["!cols"] = [
        { wch: 5 },
        { wch: 36 },
        ...exportDayColumns.map(() => ({ wch: 16 })),
        { wch: 8 },
        { wch: 8 },
        { wch: 8 },
        { wch: 10 },
      ];

      worksheet["!views"] = [
        {
          state: "frozen",
          xSplit: 2,
          ySplit: 1,
          topLeftCell: "C2",
          activePane: "bottomRight",
        },
      ];

      const worksheetRef = worksheet["!ref"];
      if (worksheetRef) {
        const range = XLSX.utils.decode_range(worksheetRef);
        for (let R = range.s.r; R <= range.e.r; R += 1) {
          for (let C = range.s.c; C <= range.e.c; C += 1) {
            const addr = XLSX.utils.encode_cell({ r: R, c: C });
            const cell = worksheet[addr];
            if (!cell) continue;
            cell.s = {
              alignment: {
                vertical: "top",
                wrapText: true,
                horizontal: C <= 1 ? "left" : "center",
              },
              font: { sz: 10 },
              border: {
                top: { style: "thin", color: { rgb: "C7D2E3" } },
                bottom: { style: "thin", color: { rgb: "C7D2E3" } },
                left: { style: "thin", color: { rgb: "C7D2E3" } },
                right: { style: "thin", color: { rgb: "C7D2E3" } },
              },
            };
          }
        }

        for (let C = 0; C < header.length; C += 1) {
          const addr = XLSX.utils.encode_cell({ r: 0, c: C });
          const cell = worksheet[addr];
          if (!cell) continue;
          cell.s = {
            font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "2F5597" } },
            alignment: {
              vertical: "center",
              horizontal: "center",
              wrapText: true,
            },
            border: {
              top: { style: "thin", color: { rgb: "C7D2E3" } },
              bottom: { style: "thin", color: { rgb: "C7D2E3" } },
              left: { style: "thin", color: { rgb: "C7D2E3" } },
              right: { style: "thin", color: { rgb: "C7D2E3" } },
            },
          };
        }
      }

      const baseRowHeight = (lineCount: number) => ({
        hpt: 16 + lineCount * 14,
      });

      worksheet["!rows"] = [
        { hpt: 28 },
        ...rowsForExport.map((row) => {
          const nameLines =
            1 + (row.department ? 1 : 0) + (row.userEmail ? 1 : 0);
          const maxDayLines = exportDayColumns.reduce((max, day) => {
            const sessions = row.sessionsByDay[day] ?? [];
            if (sessions.length === 0) return Math.max(max, 1);
            const lines = sessions.reduce(
              (sum, s) => sum + 2 + (getSessionLateText(s, t) ? 1 : 0),
              0
            );
            return Math.max(max, lines);
          }, 1);
          return baseRowHeight(Math.max(nameLines, maxDayLines));
        }),
      ];

      const fileNameSuffix =
        userIds && userIds.size < matrixRows.length ? "-chon-loc" : "";
      const workbook = XLSX.utils.book_new();
      const usedSheetNames = new Set<string>();
      const summarySheetName = t("staffAttendance.export.summarySheetName");
      usedSheetNames.add(summarySheetName);
      XLSX.utils.book_append_sheet(workbook, worksheet, summarySheetName);
      rowsForExport.forEach((staffRow) => {
        appendPersonalAttendanceSheet(
          workbook,
          staffRow,
          year,
          month,
          selectedMonth,
          dayColumns,
          hideEmptyDays,
          t,
          locale,
          usedSheetNames
        );
      });
      XLSX.writeFile(
        workbook,
        `bang-cham-cong-${month}-${year}${fileNameSuffix}.xlsx`
      );
    },
    [
      dayColumns,
      getRowStats,
      hideEmptyDays,
      locale,
      matrixRows,
      rowHasAttendanceOnDay,
      selectedMonth,
      selectedStaffId,
      t,
    ]
  );

  return (
    <div className="mt-5 space-y-5">
      <SectionHeader
        title={t("staffAttendance.title")}
        description={t("staffAttendance.description")}
        actions={
          <ActionButton
            onClick={() => {
              if (selectedStaffId === "all" && !isSelfOnlyScope) {
                setSelectedExportIds(
                  new Set(matrixRows.map((row) => row.userId))
                );
                setModalSearch("");
                setModalClassFilter("all");
                setModalDepartmentFilter("all");
                setExportModalOpen(true);
              } else {
                handleExportExcel();
              }
            }}
            disabled={loading || matrixRows.length === 0}
          >
            {t("staffAttendance.exportExcel")}
          </ActionButton>
        }
        className="!mb-0"
      />

      <Card extra="w-full p-5">
        {/* Filters Grid - 4 columns */}
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase text-ink">
              {t("staffAttendance.filter.month")}
            </label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(event.target.value)}
              className="w-full rounded border border-line px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase text-ink">
              {t("staffAttendance.filter.staff")}
            </label>
            <select
              value={selectedStaffId}
              onChange={(event) => setSelectedStaffId(event.target.value)}
              disabled={isSelfOnlyScope}
              className={`w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 ${
                isSelfOnlyScope
                  ? "cursor-not-allowed bg-gray-100 text-ink-muted"
                  : ""
              }`}
            >
              {isSelfOnlyScope ? (
                <option value={currentUser?.id || "all"}>
                  {t("staffAttendance.selfOption", {
                    name: currentUser?.full_name ?? "",
                  })}
                </option>
              ) : (
                <>
                  <option value="all">
                    {t("staffAttendance.filter.allStaff")}
                  </option>
                  {staffUsers.map((item) => (
                    <option key={item.userId} value={item.userId}>
                      {item.userCode} - {item.userName}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase text-ink">
              {t("staffAttendance.filter.class")}
            </label>
            <select
              value={classId}
              onChange={(event) => setClassId(event.target.value)}
              className="w-full rounded border border-line px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            >
              <option value="all">
                {t("staffAttendance.filter.allClasses")}
              </option>
              {classes.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase text-ink">
              {t("staffAttendance.filter.role")}
            </label>
            <select
              value={roleFilter}
              onChange={(event) =>
                setRoleFilter(event.target.value as StaffFilterRole)
              }
              className="w-full rounded border border-line px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            >
              <option value="all">
                {t("staffAttendance.filter.allRoles")}
              </option>
              <option value="teacher">{t(ROLE_LABEL.teacher)}</option>
              <option value="trainer">{t(ROLE_LABEL.trainer)}</option>
            </select>
          </div>
        </div>

        {error ? <p className="mt-4 text-sm text-red-500">{t(error)}</p> : null}

        {/* Status Legend */}
        <div className="mt-3 border-t border-line pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <p className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-ink">
              {t("staffAttendance.legend")}
            </p>
            {STATUS_ORDER.map((status) => (
              <span
                key={status}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-all ${STATUS_CELL_CLASS[status]}`}
              >
                <span className="inline-block min-w-[14px] text-center text-xs font-bold">
                  {t(STATUS_SHORT_LABEL[status])}
                </span>
                <span>{t(STATUS_LABEL[status])}</span>
              </span>
            ))}
            <span className="inline-flex items-center gap-1 rounded-full border border-line bg-surface-muted px-2.5 py-1 text-[11px] font-medium text-gray-700">
              <span className="font-bold">8h</span>
              <span>{t("staffAttendance.legendHours")}</span>
            </span>
          </div>
          <label className="mt-2 flex w-fit items-center gap-2 text-xs font-medium text-ink">
            <input
              type="checkbox"
              checked={hideEmptyDays}
              onChange={(event) => setHideEmptyDays(event.target.checked)}
              className="h-4 w-4 rounded border-line text-brand-500 focus:ring-brand-500"
            />
            {t("staffAttendance.hideEmptyDays")}
          </label>
        </div>
      </Card>

      {selectedStaffId === "all" ? (
        <Card extra="w-full p-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-1 flex-wrap items-center gap-x-5 gap-y-2">
              {summaryItems.map((item) => (
                <div
                  key={item.key}
                  className="flex items-center gap-1.5 whitespace-nowrap text-sm"
                >
                  <p className="text-xs font-semibold text-ink-muted">
                    {item.label}
                  </p>
                  <span className="text-ink-subtle">:</span>
                  <p className={`text-lg font-bold ${item.valueClassName}`}>
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
            <div className="w-full xl:w-[320px] xl:min-w-[320px]">
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("staffAttendance.searchPlaceholder")}
                className="w-full rounded border border-line px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </div>
        </Card>
      ) : null}
      <Card extra="w-full pb-2">
        {/* SHOW VERTICAL DETAIL VIEW WHEN ONE PERSON IS SELECTED (explicitly
            or because filters narrowed the result to exactly one person) */}
        {showSingleStaffView ? (
          <div className="space-y-3">
            {loading ? (
              <div className="flex flex-col items-center justify-center rounded-2xl border border-line bg-surface py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-500" />
                <p className="mt-3 text-sm text-ink-muted">
                  {t("staffAttendance.loadingTimesheet")}
                </p>
              </div>
            ) : matrixRows.length === 0 ? (
              <EmptyState
                icon={
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                }
                title={t("staffAttendance.emptyTitle")}
                description={t("staffAttendance.emptyDescription")}
                className="!rounded-2xl"
              />
            ) : (
              matrixRows.map((row) => (
                <div
                  key={row.key}
                  className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm"
                >
                  {/* Staff Header */}
                  <div className="border-b border-line bg-gradient-to-br from-brand-50 via-white to-blue-50/60 p-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex min-w-0 items-center gap-4">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-600 text-xl font-bold text-white shadow-md ring-2 ring-white">
                          {row.userName.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-xl font-bold text-ink">
                            {row.userName}
                          </p>
                          <p className="truncate text-sm font-medium text-ink">
                            {t(row.department)}
                          </p>
                          <p className="truncate text-xs text-ink-muted">
                            {row.userCode} · {row.userEmail}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {[
                          {
                            key: "present",
                            label: t("staffAttendance.stat.present"),
                            value: String(getRowStats(row).present),
                            valueClassName: "text-green-600 dark:text-green-300",
                            borderClass: "border-green-200 dark:border-green-500/30",
                          },
                          {
                            key: "lateSessions",
                            label: t("staffAttendance.stat.lateSessions"),
                            value: String(getRowStats(row).lateSessions),
                            valueClassName: "text-amber-600 dark:text-amber-300",
                            borderClass: "border-amber-200 dark:border-amber-500/30",
                          },
                          {
                            key: "lateMinutes",
                            label: t("staffAttendance.stat.lateMinutes"),
                            value: formatMinutes(
                              getRowStats(row).lateMinutes,
                              t
                            ),
                            valueClassName: "text-orange-600 dark:text-orange-300",
                            borderClass: "border-orange-200 dark:border-orange-500/30",
                          },
                          {
                            key: "absent",
                            label: t("staffAttendance.stat.absent"),
                            value: String(getRowStats(row).absent),
                            valueClassName: "text-red-600 dark:text-red-300",
                            borderClass: "border-red-200 dark:border-red-500/30",
                          },
                          {
                            key: "leave",
                            label: t("staffAttendance.stat.leave"),
                            value: String(getRowStats(row).leave),
                            valueClassName: "text-blue-600 dark:text-blue-300",
                            borderClass: "border-blue-200 dark:border-blue-500/30",
                          },
                        ].map((item) => (
                          <div
                            key={item.key}
                            className={`min-w-[68px] rounded-lg border ${item.borderClass} bg-white/85 px-2.5 py-1.5 text-center shadow-sm backdrop-blur`}
                          >
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
                              {item.label}
                            </p>
                            <p
                              className={`mt-0.5 text-lg font-bold leading-tight ${item.valueClassName}`}
                            >
                              {item.value}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Danh sách ca dạy — chỉ hiện ngày có ca, không cần bấm mở rộng */}
                  <div className="p-3">
                    {(() => {
                      const daysWithSessions = hideEmptyDays
                        ? getVisibleDaysForRow(row)
                        : dayColumns.filter(
                            (day) => (row.sessionsByDay[day] ?? []).length > 0
                          );
                      if (daysWithSessions.length === 0) {
                        return (
                          <p className="py-6 text-center text-sm text-ink-subtle">
                            {t("staffAttendance.noSessionsInMonth")}
                          </p>
                        );
                      }
                      return (
                        <div className="border-line overflow-hidden rounded-lg border">
                          <div className="border-line from-slate-100 to-slate-50 text-slate-500 grid grid-cols-[92px_minmax(0,1.4fr)_120px_100px_100px_120px] gap-3 border-b bg-gradient-to-r px-3 py-2 text-[10px] font-semibold uppercase tracking-wide">
                            <span>{t("staffAttendance.col.date")}</span>
                            <span>{t("staffAttendance.col.shiftClass")}</span>
                            <span>{t("staffAttendance.col.time")}</span>
                            <span>{t("staffAttendance.col.checkIn")}</span>
                            <span>{t("staffAttendance.col.checkOut")}</span>
                            <span>{t("staffAttendance.col.status")}</span>
                          </div>
                          {daysWithSessions.flatMap((day) => {
                            const allSessions = row.sessionsByDay[day] ?? [];
                            const sessions = hideEmptyDays
                              ? allSessions.filter(
                                  (session) => session.checkedInAt
                                )
                              : allSessions;
                            const weekday = getWeekdayLabel(
                              selectedMonth,
                              day,
                              t
                            );
                            const isToday = isTodayInMonth(selectedMonth, day);
                            return sessions.map((session, index) => {
                              const attendanceMeta = getSessionAttendanceMeta(
                                session,
                                t
                              );
                              return (
                                <div
                                  key={session.sessionId}
                                  className={`border-slate-100 grid grid-cols-[92px_minmax(0,1.4fr)_120px_100px_100px_120px] gap-3 border-b px-3 py-2.5 last:border-b-0 ${
                                    isToday ? "bg-brand-50/40" : "bg-surface"
                                  }`}
                                >
                                  <div className="text-sm font-semibold text-ink">
                                    {index === 0
                                      ? `${String(day).padStart(2, "0")}/${
                                          selectedMonth.split("-")[1]
                                        } (${weekday})`
                                      : ""}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="truncate text-sm font-semibold text-ink">
                                      {session.className}
                                    </p>
                                    <p className="text-slate-500 mt-0.5 text-xs">
                                      {session.shiftCode}
                                    </p>
                                  </div>
                                  <div className="text-slate-700 text-sm">
                                    {formatTimeRange(
                                      session.startTime,
                                      session.endTime
                                    )}
                                  </div>
                                  <div className="text-slate-700 text-sm">
                                    {formatAttendanceTime(
                                      session.checkedInAt,
                                      locale
                                    ) ?? "--:--"}
                                  </div>
                                  <div className="text-slate-700 text-sm">
                                    {formatAttendanceTime(
                                      session.checkedOutAt,
                                      locale
                                    ) ?? "--:--"}
                                  </div>
                                  <div>
                                    <span
                                      className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${attendanceMeta.className}`}
                                    >
                                      {attendanceMeta.label}
                                    </span>
                                  </div>
                                </div>
                              );
                            });
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <>
            {/* DESKTOP SPLIT VIEW - fixed staff pane + horizontally scrollable day pane */}
            <div className="border-line hidden overflow-hidden rounded-2xl border bg-surface shadow-sm lg:block">
              {loading ? (
                <p className="py-10 text-center text-sm text-ink-subtle">
                  {t("state.loading")}
                </p>
              ) : matrixRows.length === 0 ? (
                <p className="py-10 text-center text-sm text-ink-subtle">
                  {t("staffAttendance.noMatchingData")}
                </p>
              ) : (
                <div className="flex">
                  <div className="border-line w-[282px] shrink-0 border-r bg-surface shadow-[8px_0_12px_-12px_rgba(15,23,42,0.7)]">
                    <div className="grid h-[58px] grid-cols-[44px_160px_78px] border-b-2 border-line text-[10px] font-bold uppercase text-ink">
                      <div className="flex items-center justify-center border-r border-line px-2">
                        {t("staffAttendance.col.index")}
                      </div>
                      <div className="flex items-center border-r border-line px-3">
                        {t("staffAttendance.col.fullName")}
                      </div>
                      <div className="flex items-center justify-center px-1.5 text-center">
                        {t("staffAttendance.col.total")}
                      </div>
                    </div>
                    {matrixRows.map((row, index) => {
                      const stats = getRowStats(row);
                      const maxSessionsInDay = Math.max(
                        0,
                        ...visibleDayColumns.map(
                          (day) => row.sessionsByDay[day]?.length ?? 0
                        )
                      );
                      const rowMinHeight = Math.max(
                        52,
                        maxSessionsInDay * 36 + 10
                      );
                      const rowBg =
                        index % 2 === 0 ? "bg-surface" : "bg-surface-muted";
                      return (
                        <div
                          key={`${row.key}-fixed`}
                          className={`grid grid-cols-[44px_160px_78px] border-b border-line text-xs ${rowBg}`}
                          style={{ minHeight: rowMinHeight }}
                        >
                          <div className="flex items-center justify-center border-r border-line px-2 font-medium text-ink-muted">
                            {index + 1}
                          </div>
                          <div className="flex min-w-0 flex-col justify-center gap-0.5 border-r border-line px-3 text-ink">
                            <div className="truncate font-semibold">
                              {row.userName}
                            </div>
                            <div className="truncate text-[11px] font-medium leading-4 text-ink">
                              {t(row.department)}
                            </div>
                            <div className="truncate text-[10px] leading-4 text-ink-muted">
                              {row.userEmail}
                            </div>
                          </div>
                          <div className="flex items-center px-1.5">
                            <div className="grid w-full grid-cols-2 gap-x-1 gap-y-0.5 text-[10px] leading-4">
                              <div className="flex items-center justify-between gap-1">
                                <span className="font-semibold uppercase text-ink-muted">
                                  {t("staffAttendance.abbr.present")}
                                </span>
                                <span className="font-bold text-green-700 dark:text-green-300">
                                  {stats.present}
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-1">
                                <span className="font-semibold uppercase text-ink-muted">
                                  {t("staffAttendance.abbr.absent")}
                                </span>
                                <span className="font-bold text-red-700 dark:text-red-300">
                                  {stats.absent}
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-1">
                                <span className="font-semibold uppercase text-ink-muted">
                                  {t("staffAttendance.abbr.leave")}
                                </span>
                                <span className="font-bold text-amber-700 dark:text-amber-300">
                                  {stats.leave}
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-1">
                                <span className="font-semibold uppercase text-ink-muted">
                                  {t("staffAttendance.abbr.late")}
                                </span>
                                <span className="font-bold text-orange-600 dark:text-orange-300">
                                  {formatMinutes(stats.lateMinutes, t)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="min-w-0 flex-1 overflow-x-auto">
                    <div
                      style={{
                        width: `${visibleDayColumns.length * 92}px`,
                      }}
                    >
                      <div
                        className="grid h-[58px] border-b-2 border-line"
                        style={{
                          gridTemplateColumns: `repeat(${visibleDayColumns.length}, 92px)`,
                        }}
                      >
                        {visibleDayColumns.map((day) => {
                          const weekday = getWeekdayLabel(
                            selectedMonth,
                            day,
                            t
                          );
                          const isWeekend = isWeekendDay(selectedMonth, day);
                          const isStrongDivider =
                            day === 1 ||
                            getWeekdayIndex(selectedMonth, day) === 1;
                          return (
                            <div
                              key={day}
                              className={`flex flex-col items-center justify-center border-r border-gray-200 px-1 text-center text-[10px] font-bold ${
                                isStrongDivider
                                  ? "border-l-2 border-l-gray-300"
                                  : ""
                              } ${
                                isWeekend
                                  ? "bg-amber-50/60 text-amber-700 dark:text-amber-300"
                                  : "bg-surface text-ink"
                              }`}
                            >
                              <div>{String(day).padStart(2, "0")}</div>
                              <div className="text-[9px] font-normal text-ink-muted">
                                {weekday}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {matrixRows.map((row, index) => {
                        const maxSessionsInDay = Math.max(
                          0,
                          ...visibleDayColumns.map(
                            (day) => row.sessionsByDay[day]?.length ?? 0
                          )
                        );
                        const rowMinHeight = Math.max(
                          52,
                          maxSessionsInDay * 36 + 10
                        );
                        return (
                          <div
                            key={`${row.key}-days`}
                            className="group grid border-b border-line"
                            style={{
                              gridTemplateColumns: `repeat(${visibleDayColumns.length}, 92px)`,
                              minHeight: rowMinHeight,
                            }}
                          >
                            {visibleDayColumns.map((day) => {
                              const isWeekend = isWeekendDay(
                                selectedMonth,
                                day
                              );
                              const isStrongDivider =
                                day === 1 ||
                                getWeekdayIndex(selectedMonth, day) === 1;
                              const cell = getCellValue(row, day);
                              const cellKey = `${row.key}-${day}`;
                              const daySessions = row.sessionsByDay[day] ?? [];
                              const sessions = hideEmptyDays
                                ? daySessions.filter((s) => s.checkedInAt)
                                : daySessions;
                              const displayStatus =
                                hideEmptyDays && sessions.length === 0
                                  ? "unmarked"
                                  : cell.status;
                              const isOpen = openCellKey === cellKey;
                              return (
                                <div
                                  key={cellKey}
                                  className={`relative p-0.5 transition-colors group-hover:bg-blue-50/40 ${
                                    isWeekend ? "bg-amber-50/30" : "bg-surface"
                                  } ${
                                    isStrongDivider
                                      ? "border-l-2 border-l-gray-300"
                                      : ""
                                  } border-r border-r-gray-200`}
                                >
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (isOpen) {
                                        setOpenCellKey(null);
                                        return;
                                      }
                                      setOpenCellKey(cellKey);
                                    }}
                                    className={`hover:bg-slate-50/70 block min-h-[42px] w-full rounded-md px-1 py-0.5 text-left transition ${
                                      isOpen
                                        ? "bg-slate-50/70 ring-slate-200 ring-1 ring-inset"
                                        : ""
                                    }`}
                                  >
                                    {sessions.length > 0 ? (
                                      <div className="space-y-0.5">
                                        {sessions.map((session) => {
                                          const attendanceMeta =
                                            getSessionAttendanceMeta(
                                              session,
                                              t
                                            );
                                          const lateLabel =
                                            attendanceMeta.kind === "late"
                                              ? attendanceMeta.label
                                              : null;
                                          return (
                                            <div
                                              key={`${cellKey}-${session.sessionId}`}
                                              className={`rounded-md border px-1 py-0.5 ${attendanceMeta.className}`}
                                            >
                                              <div className="min-w-0 space-y-0.5">
                                                <div className="leading-3.5 truncate text-[9px] font-semibold opacity-80">
                                                  {session.className}
                                                </div>
                                                <div className="leading-3.5 truncate text-[10px] font-semibold">
                                                  {formatAttendanceTime(
                                                    session.checkedInAt,
                                                    locale
                                                  ) ?? "--:--"}
                                                  <span className="mx-1 text-ink-subtle">
                                                    /
                                                  </span>
                                                  {formatAttendanceTime(
                                                    session.checkedOutAt,
                                                    locale
                                                  ) ?? "--:--"}
                                                </div>
                                                {lateLabel ? (
                                                  <div className="leading-3.5 truncate text-[9px] font-bold">
                                                    {lateLabel}
                                                  </div>
                                                ) : null}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <span
                                        className={`inline-flex min-h-8 min-w-9 items-center justify-center text-[11px] font-bold ${STATUS_TEXT_CLASS[displayStatus]}`}
                                      >
                                        {t(STATUS_SHORT_LABEL[displayStatus])}
                                      </span>
                                    )}
                                  </button>
                                  {isOpen ? (
                                    <CellEditor
                                      sessions={sessions}
                                      staffName={row.userName}
                                      day={day}
                                      month={selectedMonth}
                                      onClose={() => {
                                        setOpenCellKey(null);
                                      }}
                                    />
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* MOBILE VERTICAL VIEW - One Day Per Row with Dropdown (Only when "All" selected) */}
            <div className="space-y-3 lg:hidden">
              {loading ? (
                <p className="py-10 text-center text-sm text-ink-subtle">
                  {t("state.loading")}
                </p>
              ) : matrixRows.length === 0 ? (
                <p className="py-10 text-center text-sm text-ink-subtle">
                  {t("staffAttendance.noMatchingData")}
                </p>
              ) : (
                matrixRows.map((row) => (
                  <div
                    key={row.key}
                    className="overflow-hidden rounded-lg border border-line bg-surface"
                  >
                    {/* Staff Header */}
                    <div className="border-b border-line bg-surface-muted p-3">
                      <p className="text-sm font-bold text-ink">
                        {row.userName}
                      </p>
                      <p className="text-[11px] font-medium text-ink">
                        {t(row.department)}
                      </p>
                      <p className="text-xs text-ink-muted">
                        {row.userCode} · {row.userEmail}
                      </p>
                    </div>

                    {/* Days List */}
                    <div className="divide-y divide-gray-200">
                      {(() => {
                        const rowDayColumns = getVisibleDaysForRow(row);
                        if (rowDayColumns.length === 0) {
                          return (
                            <p className="py-6 text-center text-sm text-ink-subtle">
                              {t("staffAttendance.noAttendanceDaysInMonth")}
                            </p>
                          );
                        }
                        return rowDayColumns.map((day) => {
                          const daySessions = row.sessionsByDay[day] ?? [];
                          const sessions = hideEmptyDays
                            ? daySessions.filter((s) => s.checkedInAt)
                            : daySessions;
                          const cell = getCellValue(row, day);
                          const displayStatus =
                            hideEmptyDays && sessions.length === 0
                              ? "unmarked"
                              : cell.status;
                          const dayHours = getDayWorkedHours(sessions);
                          const weekday = getWeekdayLabel(
                            selectedMonth,
                            day,
                            t
                          );
                          const cellKey = `${row.key}-${day}`;
                          const isExpanded = expandedDays.has(cellKey);
                          const weekendClass = isWeekendDay(selectedMonth, day)
                            ? "bg-amber-50/40"
                            : "";

                          return (
                            <div key={cellKey} className={`${weekendClass}`}>
                              {/* Day Row Header */}
                              <button
                                type="button"
                                onClick={() => {
                                  const newSet = new Set(expandedDays);
                                  if (newSet.has(cellKey)) {
                                    newSet.delete(cellKey);
                                  } else {
                                    newSet.add(cellKey);
                                  }
                                  setExpandedDays(newSet);
                                }}
                                className="flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-gray-50/50"
                              >
                                {/* Date Info */}
                                <div className="flex-1 text-left">
                                  <p className="text-xs font-semibold text-ink">
                                    {String(day).padStart(2, "0")}/
                                    {selectedMonth.split("-")[1]} ({weekday})
                                  </p>
                                  <p className="text-[11px] text-ink-muted">
                                    {sessions.length > 0
                                      ? t("staffAttendance.daySummary", {
                                          count: sessions.length,
                                          hours: formatHours(dayHours),
                                        })
                                      : t("staffAttendance.noSessions")}
                                  </p>
                                </div>

                                {/* Status & Expand Icon */}
                                <div className="ml-2 flex items-center gap-2">
                                  <StatusBadge
                                    label={t(STATUS_SHORT_LABEL[displayStatus])}
                                    tone={STATUS_BADGE_TONE[displayStatus]}
                                    className="!shrink-0 !px-2 !py-1 !text-[10px]"
                                  />
                                  <svg
                                    className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${
                                      isExpanded ? "rotate-180" : ""
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 14l-7 7m0 0l-7-7m7 7V3"
                                    />
                                  </svg>
                                </div>
                              </button>

                              {/* Sessions Dropdown Content */}
                              {isExpanded && (
                                <div className="space-y-2 border-t border-line bg-gray-50/50 px-4 py-3">
                                  {sessions.length > 0 ? (
                                    sessions.map((session) => (
                                      <div
                                        key={session.sessionId}
                                        className="rounded border border-line bg-surface p-2.5"
                                      >
                                        <div className="mb-1 flex items-start justify-between gap-2">
                                          <div className="min-w-0 flex-1">
                                            <p className="truncate text-xs font-semibold text-ink">
                                              {session.className}
                                            </p>
                                            <p className="mt-0.5 text-[11px] text-ink">
                                              {formatTimeRange(
                                                session.startTime,
                                                session.endTime
                                              )}
                                            </p>
                                          </div>
                                          <span
                                            className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-bold ${
                                              STATUS_TEXT_CLASS[session.status]
                                            }`}
                                          >
                                            {t(STATUS_LABEL[session.status])}
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-between border-t border-line pt-1 text-[10px]">
                                          <span className="text-ink-muted">
                                            {formatHours(session.hours)}h
                                            {session.status === "checked_in" ||
                                            session.status === "checked_out" ? (
                                              <span className="ml-1 text-ink-subtle">
                                                {formatAttendanceTime(
                                                  session.checkedInAt ??
                                                    session.checkedOutAt,
                                                  locale
                                                )}
                                              </span>
                                            ) : null}
                                          </span>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <p className="py-2 text-center text-[11px] text-ink-subtle">
                                      {t("staffAttendance.noSessionsInDay")}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>

                    {/* Staff Summary Footer */}
                    <div className="grid grid-cols-3 gap-2 border-t border-line bg-surface-muted px-4 py-3 text-center">
                      {(() => {
                        const stats = getRowStats(row);
                        return (
                          <>
                            <div>
                              <p className="text-[10px] font-semibold uppercase text-ink-muted">
                                {t("staffAttendance.stat.present")}
                              </p>
                              <p className="text-sm font-bold text-green-600 dark:text-green-300">
                                {stats.present}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-semibold uppercase text-ink-muted">
                                {t("staffAttendance.stat.absent")}
                              </p>
                              <p className="text-sm font-bold text-red-600 dark:text-red-300">
                                {stats.absent}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] font-semibold uppercase text-ink-muted">
                                {t("staffAttendance.stat.leave")}
                              </p>
                              <p className="text-sm font-bold text-amber-600 dark:text-amber-300">
                                {stats.leave}
                              </p>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </Card>

      <p className="text-xs text-ink-muted">
        {isSelfOnlyScope
          ? t("staffAttendance.hint.selfOnly")
          : selectedStaffId !== "all"
          ? t("staffAttendance.hint.selectedStaff")
          : t("staffAttendance.hint.all")}
      </p>

      {exportModalOpen
        ? ReactDOM.createPortal(
            <div
              className="bg-slate-900/35 fixed inset-0 z-[1000] flex items-center justify-center px-4"
              onClick={() => setExportModalOpen(false)}
            >
              <div
                role="dialog"
                aria-modal="true"
                className="w-full max-w-md rounded-xl border border-line bg-surface p-5 text-left shadow-2xl"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-bold text-ink">
                      {t("staffAttendance.exportModal.title")}
                    </h3>
                    <p className="mt-1 text-xs text-ink-muted">
                      {t("staffAttendance.exportModal.description")}
                    </p>
                  </div>
                  <ActionButton
                    variant="ghost"
                    size="sm"
                    onClick={() => setExportModalOpen(false)}
                    className="!rounded-md !px-2 !py-1 text-ink-subtle hover:text-gray-600"
                  >
                    {t("action.close")}
                  </ActionButton>
                </div>

                <div className="mb-3 space-y-2">
                  <input
                    type="text"
                    value={modalSearch}
                    onChange={(event) => setModalSearch(event.target.value)}
                    placeholder={t(
                      "staffAttendance.exportModal.searchPlaceholder"
                    )}
                    className="w-full rounded border border-line px-3 py-1.5 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={modalClassFilter}
                      onChange={(event) =>
                        setModalClassFilter(event.target.value)
                      }
                      className="w-full rounded border border-line px-2 py-1.5 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    >
                      <option value="all">
                        {t("staffAttendance.filter.allClasses")}
                      </option>
                      {Array.from(
                        new Set(
                          matrixRows
                            .map((row) => row.className)
                            .filter((name) => name && name.trim() !== "")
                        )
                      ).map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={modalDepartmentFilter}
                      onChange={(event) =>
                        setModalDepartmentFilter(event.target.value)
                      }
                      className="w-full rounded border border-line px-2 py-1.5 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    >
                      <option value="all">
                        {t("staffAttendance.exportModal.allPositions")}
                      </option>
                      {Array.from(
                        new Set(
                          matrixRows
                            .map((row) => row.department)
                            .filter((dept) => dept && dept.trim() !== "")
                        )
                      ).map((dept) => (
                        <option key={dept} value={dept}>
                          {t(dept)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {(() => {
                  const search = modalSearch.trim().toLowerCase();
                  const filteredRows = matrixRows.filter((row) => {
                    if (search) {
                      const haystack = [
                        row.userName,
                        row.userCode,
                        row.userEmail,
                      ]
                        .join(" ")
                        .toLowerCase();
                      if (!haystack.includes(search)) return false;
                    }
                    if (
                      modalClassFilter !== "all" &&
                      row.className !== modalClassFilter
                    ) {
                      return false;
                    }
                    if (
                      modalDepartmentFilter !== "all" &&
                      row.department !== modalDepartmentFilter
                    ) {
                      return false;
                    }
                    return true;
                  });
                  const visibleIds = filteredRows.map((row) => row.userId);

                  return (
                    <>
                      <div className="mb-2 flex items-center justify-between gap-2 text-xs">
                        <span className="font-semibold text-ink">
                          {filteredRows.length < matrixRows.length
                            ? t(
                                "staffAttendance.exportModal.selectedFiltered",
                                {
                                  selected: selectedExportIds.size,
                                  total: matrixRows.length,
                                  shown: filteredRows.length,
                                }
                              )
                            : t("staffAttendance.exportModal.selected", {
                                selected: selectedExportIds.size,
                                total: matrixRows.length,
                              })}
                        </span>
                        <div className="flex gap-1">
                          <ActionButton
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              const next = new Set(selectedExportIds);
                              visibleIds.forEach((id) => next.add(id));
                              setSelectedExportIds(next);
                            }}
                            className="!rounded !border-gray-300 !px-2 !py-1 !text-[11px] text-ink"
                          >
                            {t("staffAttendance.exportModal.selectVisible")}
                          </ActionButton>
                          <ActionButton
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              const next = new Set(selectedExportIds);
                              visibleIds.forEach((id) => next.delete(id));
                              setSelectedExportIds(next);
                            }}
                            className="!rounded !border-gray-300 !px-2 !py-1 !text-[11px] text-ink"
                          >
                            {t("staffAttendance.exportModal.deselectVisible")}
                          </ActionButton>
                        </div>
                      </div>

                      <div className="max-h-72 space-y-1 overflow-auto rounded-md border border-line bg-gray-50/60 p-1">
                        {filteredRows.length === 0 ? (
                          <p className="py-6 text-center text-xs text-ink-subtle">
                            {t("staffAttendance.exportModal.noMatch")}
                          </p>
                        ) : (
                          filteredRows.map((row) => {
                            const checked = selectedExportIds.has(row.userId);
                            return (
                              <label
                                key={row.userId}
                                className={`flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 transition ${
                                  checked
                                    ? "bg-brand-50/60 ring-1 ring-inset ring-brand-200"
                                    : "bg-surface hover:bg-surface-hover"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(event) => {
                                    const next = new Set(selectedExportIds);
                                    if (event.target.checked) {
                                      next.add(row.userId);
                                    } else {
                                      next.delete(row.userId);
                                    }
                                    setSelectedExportIds(next);
                                  }}
                                  className="mt-1 h-4 w-4 rounded border-line text-brand-500 focus:ring-brand-500"
                                />
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-semibold text-ink">
                                    {row.userName}
                                  </p>
                                  <p className="truncate text-[11px] text-ink-muted">
                                    {row.userCode}
                                    {row.department
                                      ? ` · ${t(row.department)}`
                                      : ""}
                                    {row.className ? ` · ${row.className}` : ""}
                                  </p>
                                </div>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </>
                  );
                })()}

                <div className="mt-4 flex justify-end gap-2">
                  <ActionButton
                    variant="secondary"
                    size="sm"
                    onClick={() => setExportModalOpen(false)}
                    className="!rounded-md !border-gray-300 !py-1.5 text-ink"
                  >
                    {t("action.cancel")}
                  </ActionButton>
                  <ActionButton
                    size="sm"
                    disabled={selectedExportIds.size === 0}
                    onClick={() => {
                      handleExportExcel(selectedExportIds);
                      setExportModalOpen(false);
                    }}
                    className="!rounded-md !py-1.5 disabled:!bg-gray-300"
                  >
                    {t("staffAttendance.exportExcelCount", {
                      count: selectedExportIds.size,
                    })}
                  </ActionButton>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default StaffAttendance;
